import type { CreditCardEntryZoidComponent } from 'src/utilities/creditcard-iframe-types';

export interface GetCreditCardEntryZoidComponentProps {
  /** Maximum number of polling attempts before giving up */
  maxPollingRetries?: number;
  /** Time in milliseconds between each polling attempt */
  pollingIntervalMs?: number;
  /** Optional AbortSignal to cancel the polling */
  abortSignal?: AbortSignal;
}

/**
 * Returns a Promise which resolves with the init function
 * of ghsPayments.CreditCardEntryZoidComponent if it becomes available,
 * or rejects if the maximum number of retries is exceeded.
 */
export function getCreditCardEntryZoidComponent({
  maxPollingRetries = 10,
  pollingIntervalMs = 500,
  abortSignal,
}: GetCreditCardEntryZoidComponentProps = {}): Promise<CreditCardEntryZoidComponent> {
  return new Promise<CreditCardEntryZoidComponent>((resolve, reject) => {
    if (abortSignal?.aborted) {
      reject(new Error('Credit card component polling was aborted'));
      return;
    }

    let currentTimeout: NodeJS.Timeout;

    const checkComponent = (
      attempts: number,
      prevTimeout?: NodeJS.Timeout
    ): void => {
      if (prevTimeout) {
        clearTimeout(prevTimeout);
      }

      const component = window.ghsPayments?.CreditCardEntryZoidComponent;

      if (typeof component?.init === 'function') {
        resolve(component);
        return;
      }

      if (attempts >= maxPollingRetries) {
        reject(
          new Error(
            'ghsPayments.CreditCardEntryZoidComponent not available after maximum retries.'
          )
        );
        return;
      }

      currentTimeout = setTimeout(
        () => checkComponent(attempts + 1, currentTimeout),
        pollingIntervalMs
      ) as unknown as NodeJS.Timeout;
    };

    abortSignal?.addEventListener(
      'abort',
      () => {
        if (currentTimeout) {
          clearTimeout(currentTimeout);
        }
        reject(new Error('Credit card component polling was aborted'));
      },
      { once: true }
    );

    checkComponent(0);
  });
}
